/* eslint-disable react-refresh/only-export-components -- `computeTotals` and `useGroupCollapse` are
   the table's contract, not helpers that happen to live here: the parent's "collapse all" control
   and its export payload must agree with what the table renders, and moving them out is how they
   stop agreeing. Fast Refresh gives up on this file. */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, ChevronsUpDown, ArrowDown, ArrowUp } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { Tooltip } from '../ui/Tooltip';
import { cn } from '../../utils/cn';
import { formatByKind } from './reportFormat';
import type { ReportColumn, ReportColumnTotal, ReportGroup } from './types';

/**
 * The report table.
 *
 * It is one table, not four: flat rows or grouped rows, subtotals, a per-currency grand total, an
 * optional sort, and the sticky first column all come from the same `ReportColumn[]`. That matters
 * because the previous per-screen tables each re-derived their own totals, and the totals in the
 * footer, the totals in the PDF and the totals in the XLSX were three separate implementations.
 *
 * Two things carry the colour, because a payroll register with no colour is unreadable at a glance:
 * a `bg-primary/5` band on every group header, and the group's subtotals rendered INSIDE that band,
 * under their own columns. A collapsed group therefore still answers the question it was opened to
 * answer. Data keeps the semantic tones (status chips, the caller's `render`); chrome does not.
 */

/* ── totals ───────────────────────────────────────────────────────────────── */

/** One totals line. `currency` is `''` for the currency-free columns (days, hours, counts). */
export interface ReportTotalsRow {
  currency: string;
  cells: Record<string, number | null>;
}

function totalMode<Row>(column: ReportColumn<Row>): ReportColumnTotal {
  return column.total ?? (column.kind === 'money' ? 'sum' : 'none');
}

/** True when at least one column contributes to a total, i.e. a footer is worth rendering. */
export function hasTotals<Row>(columns: ReportColumn<Row>[]): boolean {
  return columns.some((c) => totalMode(c) !== 'none');
}

/**
 * Per-currency totals.
 *
 * Days, hours and counts are currency-free and always total across every row. Money is only
 * meaningful within one currency, so a mixed-currency result gets one line for the currency-free
 * columns plus one line per currency for the money columns. Rows carrying no currency hold no money
 * and never get a line of their own.
 */
export function computeTotals<Row>(
  rows: Row[],
  columns: ReportColumn<Row>[],
  currencyOf?: (row: Row) => string | undefined,
): ReportTotalsRow[] {
  if (rows.length === 0) return [];

  const aggregate = (subset: Row[], column: ReportColumn<Row>): number | null => {
    const mode = totalMode(column);
    if (mode === 'none') return null;
    let sum = 0;
    let count = 0;
    for (const row of subset) {
      const value = column.value(row);
      if (typeof value === 'number' && Number.isFinite(value)) {
        sum += value;
        count += 1;
      }
    }
    if (count === 0) return null;
    return mode === 'avg' ? sum / count : sum;
  };

  const build = (
    subset: Row[],
    currency: string,
    include: (column: ReportColumn<Row>) => boolean,
  ): ReportTotalsRow => {
    const cells: Record<string, number | null> = {};
    for (const column of columns) {
      cells[column.key] = include(column) ? aggregate(subset, column) : null;
    }
    return { currency, cells };
  };

  const currencies = currencyOf
    ? Array.from(new Set(rows.map((r) => currencyOf(r) ?? '').filter(Boolean))).sort()
    : [];

  if (currencies.length <= 1) {
    return [build(rows, currencies[0] ?? '', () => true)];
  }
  return [
    build(rows, '', (c) => c.kind !== 'money'),
    ...currencies.map((currency) =>
      build(
        rows.filter((r) => (currencyOf?.(r) ?? '') === currency),
        currency,
        (c) => c.kind === 'money',
      ),
    ),
  ];
}

/** Flattens per-currency totals into one lookup, for the single-line group header band. */
function mergeTotals(totals: ReportTotalsRow[]): Record<string, number | null> {
  const merged: Record<string, number | null> = {};
  for (const row of totals) {
    for (const [key, value] of Object.entries(row.cells)) {
      if (value !== null && merged[key] === undefined) merged[key] = value;
    }
  }
  return merged;
}

/* ── collapse state ───────────────────────────────────────────────────────── */

export interface GroupCollapseApi {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  allCollapsed: boolean;
  allExpanded: boolean;
}

/**
 * Collapse state for grouped tables, owned by the PARENT so the toolbar's "Collapse all" and the
 * table's chevrons drive the same state. Pass the result to `ReportTable`'s `collapse` prop; a
 * table given none keeps an identical instance internally.
 */
export function useGroupCollapse<Row>(
  groups: ReportGroup<Row>[] | undefined,
  defaultCollapsed = false,
): GroupCollapseApi {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const keys = useMemo(() => (groups ?? []).map((g) => g.key), [groups]);

  const isCollapsed = useCallback(
    (key: string) => overrides[key] ?? defaultCollapsed,
    [overrides, defaultCollapsed],
  );
  const toggle = useCallback(
    (key: string) =>
      setOverrides((prev) => ({ ...prev, [key]: !(prev[key] ?? defaultCollapsed) })),
    [defaultCollapsed],
  );
  const expandAll = useCallback(
    () => setOverrides(Object.fromEntries(keys.map((k) => [k, false]))),
    [keys],
  );
  const collapseAll = useCallback(
    () => setOverrides(Object.fromEntries(keys.map((k) => [k, true]))),
    [keys],
  );

  const allCollapsed = keys.length > 0 && keys.every((k) => isCollapsed(k));
  const allExpanded = keys.every((k) => !isCollapsed(k));

  return { isCollapsed, toggle, expandAll, collapseAll, allCollapsed, allExpanded };
}

/* ── table ────────────────────────────────────────────────────────────────── */

export type ReportTableDensity = 'compact' | 'comfortable';

export interface ReportTableProps<Row> {
  columns: ReportColumn<Row>[];
  /** Flat mode. Ignored when `groups` is given. */
  rows?: Row[];
  /** Grouped mode: a tinted header band per group, with that group's subtotals inside it. */
  groups?: ReportGroup<Row>[];
  rowKey: (row: Row) => string;
  /** Currency of a row's money columns. Omit for a report with no money. */
  currencyOf?: (row: Row) => string | undefined;
  /** Defaults to true whenever any column totals. */
  grandTotal?: boolean;
  /** Grouped mode only: chevrons that fold a group down to its header band. */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Share collapse state with a parent toolbar. Omit to let the table own it. */
  collapse?: GroupCollapseApi;
  /** Flat mode only. Grouped data is already ordered by its grouping. */
  sortable?: boolean;
  emptyState: React.ReactNode;
  testId?: string;
  density?: ReportTableDensity;
  /** Extra classes per row, e.g. muting a day nobody worked. */
  rowClassName?: (row: Row) => string | undefined;
  /**
   * A background utility for a row the screen is HIGHLIGHTING (a deep-link target, say).
   *
   * Separate from `rowClassName` because a sticky cell needs the tint painted as its own `::before`
   * — a `<tr>` background renders UNDER the opaque `bg-card` of a pinned cell, so a highlighted row
   * would show two un-tinted notches where the pins are.
   */
  rowTint?: (row: Row) => string | undefined;
  /**
   * Names a row for tests. Rows without one keep the shared `report-row` id, so a caller can still
   * count or sweep the table.
   */
  rowTestId?: (row: Row) => string | undefined;
  /**
   * An optional DETAIL BLOCK, rendered as an extra full-width row directly beneath its data row.
   *
   * It exists for reports whose row is a SUMMARY of something the reader may need to see in full —
   * the Clockings day row summarises a day's punches into first-in / last-out / worked, and the
   * timeline of individual punches (which is variable-length and therefore cannot be a column) is
   * disclosed here instead of being lost. Return `null`/`undefined` for a row with nothing to show
   * and NO extra row is emitted, so a table that never uses it renders exactly as before.
   *
   * The caller owns the open/closed state and the toggle — this only renders what it is handed.
   */
  renderRowDetail?: (row: Row) => React.ReactNode;
  /** Overrides the locale used for number and date formatting. Defaults to the active language. */
  locale?: string;
  className?: string;
  /** Accessible name for the table. */
  ariaLabel?: string;
  /** Caps the scroll box so the header actually sticks. Pass false for a table that flows freely. */
  stickyHeader?: boolean;
  /** The cap. Only bites once the table is taller than it, so short reports are unaffected. */
  maxBodyHeight?: string;
}

/**
 * ⚠️ THE STICKY CELL BACKGROUND IS LOAD-BEARING, NOT DECORATION. A sticky first column overlays the
 * columns sliding underneath it; without an opaque fill the two sets of numbers render on top of
 * each other. The tint on a banded row is painted by a `::before` at `-z-10`, which sits ABOVE the
 * cell's own background and BELOW its text because `position: sticky` with a z-index establishes a
 * stacking context.
 */
const STICKY_BODY_CELL = 'sticky start-0 z-10 bg-card';
const STICKY_HEAD_CELL = 'sticky start-0 z-10 bg-muted';
/**
 * The mirror pin, for a column carrying an ACTION (`ReportColumn.stickyEnd`). Logical `end-0`, so
 * in Arabic it pins the LEFT edge without a second rule: the last column really is on the left
 * there, and an `ltr:right-0` would pin the wrong side of the wrong end.
 */
const STICKY_END_BODY_CELL = 'sticky end-0 z-10 bg-card border-s border-border';
const STICKY_END_HEAD_CELL = 'sticky end-0 z-20 bg-muted border-s border-border';
const TINT_GROUP = 'before:absolute before:inset-0 before:-z-10 before:bg-primary/5';
const TINT_SUBTOTAL = 'before:absolute before:inset-0 before:-z-10 before:bg-muted/40';
const TINT_TOTAL = 'before:absolute before:inset-0 before:-z-10 before:bg-muted/50';

function isNumericKind(column: { kind: string }): boolean {
  return column.kind !== 'text' && column.kind !== 'status' && column.kind !== 'custom';
}

export function ReportTable<Row>({
  columns,
  rows,
  groups,
  rowKey,
  currencyOf,
  grandTotal,
  collapsible = true,
  defaultCollapsed = false,
  collapse,
  sortable = false,
  emptyState,
  testId,
  density = 'comfortable',
  rowClassName,
  rowTint,
  rowTestId,
  renderRowDetail,
  locale,
  className,
  ariaLabel,
  stickyHeader = true,
  maxBodyHeight = '70vh',
}: ReportTableProps<Row>) {
  const { t, i18n } = useTranslation();
  const activeLocale = locale ?? i18n.language;

  const internalCollapse = useGroupCollapse(groups, defaultCollapsed);
  const collapseApi = collapse ?? internalCollapse;

  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const grouped = !!groups;
  const flatRows = useMemo(() => rows ?? [], [rows]);

  const sortedRows = useMemo(() => {
    if (!sortable || !sort) return flatRows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return flatRows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...flatRows].sort((a, b) => {
      const av = column.value(a);
      const bv = column.value(b);
      if (av === bv) return 0;
      if (av === null || av === undefined || av === '') return 1;
      if (bv === null || bv === undefined || bv === '') return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv), activeLocale) * factor;
    });
  }, [sortable, sort, flatRows, columns, activeLocale]);

  const allRows = useMemo(
    () => (grouped ? (groups ?? []).flatMap((g) => g.totalRows ?? g.rows) : sortedRows),
    [grouped, groups, sortedRows],
  );

  const totalsEnabled = grandTotal ?? hasTotals(columns);
  const grandTotals = useMemo(
    () => (totalsEnabled ? computeTotals(allRows, columns, currencyOf) : []),
    [totalsEnabled, allRows, columns, currencyOf],
  );

  const cellPadding = density === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5';
  const isEmpty = grouped ? (groups ?? []).length === 0 : sortedRows.length === 0;

  const responsiveClass = (column: ReportColumn<Row>) =>
    column.hideBelow === 'md'
      ? 'hidden md:table-cell'
      : column.hideBelow === 'lg'
        ? 'hidden lg:table-cell'
        : undefined;

  const alignClass = (column: ReportColumn<Row>) =>
    (column.align ?? (isNumericKind(column) ? 'end' : 'start')) === 'end'
      ? 'text-end'
      : 'text-start';

  /** The highlight tint, as a `::before` so it paints ABOVE a pinned cell's opaque background. */
  const stickyTint = (row: Row) => {
    const tint = rowTint?.(row);
    return tint ? cn('before:absolute before:inset-0 before:-z-10', tint) : undefined;
  };

  const toggleSort = (key: string) =>
    setSort((prev) =>
      !prev || prev.key !== key
        ? { key, dir: 'asc' }
        : prev.dir === 'asc'
          ? { key, dir: 'desc' }
          : null,
    );

  /** A totals cell: money uses that line's currency, everything else its own kind. */
  const renderTotalsCell = (
    column: ReportColumn<Row>,
    totals: Record<string, number | null>,
    currency: string,
  ) => {
    const value = totals[column.key];
    if (value === null || value === undefined) return '';
    return formatByKind(column.kind, value, { currency, locale: activeLocale });
  };

  const renderDataCell = (column: ReportColumn<Row>, row: Row) => {
    if (column.render) return column.render(row);
    return formatByKind(column.kind, column.value(row), {
      currency: currencyOf?.(row),
      locale: activeLocale,
    });
  };

  /**
   * The leading text cell of a banded row spans up to the first column that carries a figure, so
   * the group title reads as a title rather than being crushed into one narrow column. When the
   * FIRST column itself totals, the label takes that cell and its figure is dropped: a total whose
   * own label is missing is worse than a total one column short.
   */
  const leadingSpan = (totals: Record<string, number | null>) => {
    const index = columns.findIndex((c) => totals[c.key] !== null && totals[c.key] !== undefined);
    return index === -1 ? columns.length : Math.max(index, 1);
  };

  const bandRow = (
    key: string,
    leading: React.ReactNode,
    totals: Record<string, number | null>,
    currency: string,
    tint: string,
    rowClass: string,
    testIdValue?: string,
  ) => {
    const span = leadingSpan(totals);
    return (
      <TableRow key={key} className={cn('hover:bg-transparent', rowClass)} data-testid={testIdValue}>
        <TableCell
          colSpan={span}
          className={cn(cellPadding, 'text-start align-middle', STICKY_BODY_CELL, tint)}
        >
          {leading}
        </TableCell>
        {span < columns.length &&
          columns.slice(span).map((column) => (
            <TableCell
              key={column.key}
              className={cn(
                cellPadding,
                'whitespace-nowrap align-middle',
                alignClass(column),
                isNumericKind(column) && 'tabular-nums',
                responsiveClass(column),
                // A pinned action column has to carry the BAND's colour on a band row, or the group
                // header shows a card-coloured notch where the pin floats over it.
                column.stickyEnd && cn(STICKY_END_BODY_CELL, tint),
              )}
            >
              {renderTotalsCell(column, totals, currency)}
            </TableCell>
          ))}
      </TableRow>
    );
  };

  /**
   * One data row — and, when the caller supplies a detail block for it, the full-width row that
   * carries it. Shared by the grouped and the flat branch so the two cannot drift about what a row
   * looks like (they already had two copies of the cell loop before the expander existed).
   */
  const dataRow = (row: Row) => {
    const detail = renderRowDetail?.(row);
    const cells = (
      <TableRow
        className={cn('hover:bg-muted/30', rowTint?.(row), rowClassName?.(row))}
        data-testid={rowTestId?.(row) ?? 'report-row'}
      >
        {columns.map((column, index) => (
          <TableCell
            key={column.key}
            className={cn(
              cellPadding,
              'whitespace-nowrap align-middle',
              alignClass(column),
              isNumericKind(column) && 'tabular-nums',
              column.emphasis && 'font-medium',
              column.muted && 'text-muted-foreground',
              responsiveClass(column),
              index === 0 && STICKY_BODY_CELL,
              column.stickyEnd && STICKY_END_BODY_CELL,
              (index === 0 || column.stickyEnd) && stickyTint(row),
            )}
          >
            {renderDataCell(column, row)}
          </TableCell>
        ))}
      </TableRow>
    );

    return (
      <React.Fragment key={rowKey(row)}>
        {cells}
        {detail ? (
          <TableRow className="hover:bg-transparent" data-testid="report-row-detail">
            {/* No sticky cell here on purpose: a detail block spans every column, so there is
                nothing for a pin to hold still against, and an opaque pinned cell would cut a
                notch out of it. */}
            <TableCell colSpan={columns.length} className="border-b border-border bg-muted/20 p-0">
              {detail}
            </TableCell>
          </TableRow>
        ) : null}
      </React.Fragment>
    );
  };

  /**
   * ⚠️ A STICKY HEADER NEEDS A SCROLLPORT TO STICK TO. `Table` renders its own `overflow-auto`
   * wrapper, which becomes the nearest scrolling ancestor of the `<thead>` — and a wrapper with an
   * auto height never scrolls vertically, so `sticky top-0` inside it is inert. Capping that
   * wrapper is what turns the header sticky; the cap only bites on a table taller than it.
   */
  const scrollBoxStyle = stickyHeader
    ? ({ '--report-table-max-h': maxBodyHeight } as React.CSSProperties)
    : undefined;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card',
        stickyHeader && '[&>div]:max-h-[var(--report-table-max-h)]',
        className,
      )}
      style={scrollBoxStyle}
      data-testid={testId}
    >
      <Table aria-label={ariaLabel}>
        <TableHeader className={cn(stickyHeader && 'sticky top-0 z-30', 'bg-muted')}>
          <TableRow className="hover:bg-transparent">
            {columns.map((column) => {
              const numeric = isNumericKind(column);
              const sorted = sort?.key === column.key;
              const label = (
                <span
                  className={cn(
                    'block text-xs font-medium uppercase tracking-wide',
                    alignClass(column),
                  )}
                >
                  {column.label}
                </span>
              );
              return (
                <TableHead
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'h-10 whitespace-nowrap align-middle',
                    responsiveClass(column),
                    column.key === columns[0]?.key && STICKY_HEAD_CELL,
                    column.stickyEnd && STICKY_END_HEAD_CELL,
                  )}
                >
                  {sortable && !grouped ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className={cn(
                        'flex w-full items-center gap-1 rounded transition-colors hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                        numeric && 'justify-end',
                      )}
                      title={t('reports.sortBy', { column: column.label })}
                    >
                      {label}
                      {sorted ? (
                        sort.dir === 'asc' ? (
                          <ArrowUp className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                        ) : (
                          <ArrowDown className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                        )
                      ) : (
                        <ChevronsUpDown
                          className="h-3 w-3 shrink-0 opacity-40"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ) : column.tooltip ? (
                    <Tooltip content={column.tooltip}>
                      <span className="cursor-help border-b border-dotted border-muted-foreground/50">
                        {label}
                      </span>
                    </Tooltip>
                  ) : (
                    label
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>

        <TableBody>
          {isEmpty ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="p-0">
                {emptyState}
              </TableCell>
            </TableRow>
          ) : grouped ? (
            (groups ?? []).map((group) => {
              // `totalRows` is what the group COUNTS; `rows` is what it LISTS. They differ only when
              // the screen has a client-side row filter over an unchanged period.
              const groupTotals = computeTotals(group.totalRows ?? group.rows, columns, currencyOf);
              const merged = mergeTotals(groupTotals);
              const headerCurrency = groupTotals.find((r) => r.currency)?.currency ?? '';
              const collapsed = collapsible && collapseApi.isCollapsed(group.key);

              const heading = (
                <div className="flex items-start gap-2">
                  {collapsible && (
                    <button
                      type="button"
                      onClick={() => collapseApi.toggle(group.key)}
                      aria-expanded={!collapsed}
                      aria-label={
                        collapsed
                          ? t('reports.expandGroup', { group: group.title })
                          : t('reports.collapseGroup', { group: group.title })
                      }
                      data-testid="report-group-toggle"
                      className={cn(
                        'mt-0.5 shrink-0 rounded text-muted-foreground transition-colors hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                      )}
                    >
                      {collapsed ? (
                        <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
                      ) : (
                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">{group.title}</span>
                      {group.badges}
                    </div>
                    {group.subtitle && (
                      <p className="mt-0.5 whitespace-nowrap text-xs text-muted-foreground">{group.subtitle}</p>
                    )}
                  </div>
                </div>
              );

              return (
                <React.Fragment key={group.key}>
                  {bandRow(
                    `${group.key}-head`,
                    heading,
                    merged,
                    headerCurrency,
                    TINT_GROUP,
                    'border-b border-border bg-primary/5',
                    'report-group-header',
                  )}

                  {!collapsed && group.rows.map(dataRow)}

                  {/* A single group's subtotal is the grand total repeated; print it only when there is
                      something to add up across groups. */}
                  {!collapsed && groups.length > 1 &&
                    groupTotals.map((line) =>
                      bandRow(
                        `${group.key}-sub-${line.currency || 'base'}`,
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {line.currency
                            ? t('reports.subtotalFor', { currency: line.currency })
                            : t('reports.subtotal')}
                        </span>,
                        line.cells,
                        line.currency,
                        TINT_SUBTOTAL,
                        'border-b border-border bg-muted/40 font-medium',
                        'report-subtotal-row',
                      ),
                    )}
                </React.Fragment>
              );
            })
          ) : (
            sortedRows.map(dataRow)
          )}
        </TableBody>

        {!isEmpty && grandTotals.length > 0 && (
          <TableFooter className="border-t-2 border-primary/30 bg-muted/50 font-semibold">
            {grandTotals.map((line) =>
              bandRow(
                `total-${line.currency || 'base'}`,
                <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  {line.currency
                    ? t('reports.totalFor', { currency: line.currency })
                    : t('reports.total')}
                </span>,
                line.cells,
                line.currency,
                TINT_TOTAL,
                'border-0',
                'report-total-row',
              ),
            )}
          </TableFooter>
        )}
      </Table>
    </div>
  );
}

export default ReportTable;
